import * as PIXI from 'pixi.js';
import { Signal } from 'framework/utils/Signal';

export interface IFuturePromise<T = void> {
  resolve: (resolveValue?: T) => void;
  reject: (error?: unknown) => void;
  promise: Promise<T>;
}

export function createFuturePromise<T = void>(defaultValue?: T): IFuturePromise<T> {
  let resolveFunc: (resolveValue?: T) => void;
  let rejectFunc: (error?: unknown) => void;

  const promise: Promise<T> = new Promise(
    (resolve: (resolveValue?: T) => void, reject: (error?: unknown) => void): void => {
      resolveFunc = resolve;
      rejectFunc = reject;
    },
  );

  return {
    // @ts-ignore not initialized
    resolve: resolveFunc,
    // @ts-ignore not initialized
    reject: rejectFunc,
    promise,
  };
}

export function promisifySignal(signal: Signal<any>): Promise<void> {
  return new Promise((resolve: () => void): void => {
    signal.addOnce(resolve);
  });
}

export function promisifyEvent(eventEmitter: PIXI.utils.EventEmitter, eventName: string): Promise<void> {
  return new Promise((resolve: () => void): void => {
    eventEmitter.once(eventName, resolve);
  });
}

export function* generatifySignal(signal: Signal<any>) {
  let resolved: boolean = false;
  signal.addOnce(() => (resolved = true));
  while (!resolved) {
    yield;
  }
}

export function* generatifyPromise<T = unknown>(promise: Promise<T>) {
  let resolved: boolean = false;
  let result;
  promise.then((value) => {
    resolved = true;
    result = value;
  });

  while (!resolved) {
    yield;
  }
  return result;
}

export function* yieldForEach<T = unknown>(
  array: T[],
  func: (value: T, index: number) => void | Generator,
  yieldOnlyIfGenerator: boolean = false,
) {
  for (let i = 0; i < array.length; i++) {
    const returnVal = func(array[i], i);
    if (returnVal && returnVal.next) {
      yield* returnVal;
    } else if (!yieldOnlyIfGenerator) {
      yield returnVal;
    }
  }
}

export function runGeneratorWithTicker(ticker: PIXI.ticker.Ticker, generator: Generator): void {
  const update = () => {
    if (generator.next().done) ticker.remove(update);
  };
  ticker.add(update);
}

export const waitForMs = (time: number) => {
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      resolve('done');
    }, time);
    return timer;
  });
};

export function debounce(func: (...args: unknown[]) => void, wait: number, immediate: boolean = false) {
  let timeout;

  return function executedFunction(...args) {
    // @ts-ignore
    const context = this;

    const later = () => {
      timeout = null;

      if (!immediate) {
        func.call(context, ...args);
      }
    };

    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);

    if (callNow) {
      func.call(context, ...args);
    }
  };
}
